import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { STORAGE } from '../constants/storageKeys';
import { usePersistentState } from '../hooks/usePersistentState';
import { createRngFromSeed, randomSeed, d6Unicode } from '../utils/rng';
import { copyText } from '../utils/share';
import './Dice.css';

const defaultDice = {
  preset: 'D6',
  custom: { dice: [{ sides: 6, count: 1 }], modifier: 0 },
  lastRoll: null,
  history: [],
};

function specFromPreset(preset, custom) {
  if (preset === 'D6') return { dice: [{ sides: 6, count: 1 }], modifier: 0 };
  if (preset === '2D6') return { dice: [{ sides: 6, count: 2 }], modifier: 0 };
  if (preset === 'D20') return { dice: [{ sides: 20, count: 1 }], modifier: 0 };
  const mod = Number(custom.modifier);
  return {
    dice: custom.dice && custom.dice.length ? custom.dice : [{ sides: 6, count: 1 }],
    modifier: Number.isFinite(mod) ? mod : 0,
  };
}

function buildLabel(rollsSpec, allRolls, modifier) {
  let offset = 0;
  const chunks = rollsSpec.map((spec) => {
    const r = allRolls.slice(offset, offset + spec.count);
    offset += spec.count;
    return `${spec.count}d${spec.sides} (${r.join('+')})`;
  });
  const base = allRolls.reduce((a, b) => a + b, 0);
  const modStr = modifier === 0 ? '' : modifier > 0 ? ` + ${modifier}` : ` ${modifier}`;
  return `${chunks.join(' · ')}${modStr} = ${base + modifier}`;
}

function renderFace(sides, value) {
  if (sides === 6 && value >= 1 && value <= 6) return d6Unicode(value);
  return String(value);
}

/** Build slots { sides }[] in roll order for visuals */
function specToSlots(spec) {
  const slots = [];
  spec.dice.forEach((die) => {
    for (let i = 0; i < die.count; i += 1) {
      slots.push({ sides: die.sides });
    }
  });
  return slots;
}

function randomFaceValue(sides) {
  return 1 + Math.floor(Math.random() * sides);
}

/** One fair roll (single RNG stream) — call once when animation completes */
function buildRollResult(spec) {
  const seed = randomSeed();
  const rng = createRngFromSeed(seed);
  const faces = [];
  spec.dice.forEach((die) => {
    for (let i = 0; i < die.count; i += 1) {
      const value = rng.rollDie(die.sides);
      faces.push({ sides: die.sides, value });
    }
  });
  const allRolls = faces.map((f) => f.value);
  const modifier = spec.modifier;
  const base = allRolls.reduce((a, b) => a + b, 0);
  const total = base + modifier;
  const label = buildLabel(spec.dice, [...allRolls], modifier);
  const isNat20 = faces.length === 1 && faces[0].sides === 20 && faces[0].value === 20;
  const entry = { label, ts: Date.now(), rolls: allRolls, modifier, total, seed, faces };
  return { faces, modifier, total, seed, isNat20, entry };
}

export default function Dice() {
  const [dice, setDice] = usePersistentState(STORAGE.dice, defaultDice);
  const [settings] = usePersistentState(STORAGE.settings, { reduceMotion: false, soundOn: false, hapticsOn: false });
  const [rollMode, setRollMode] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [nat20, setNat20] = useState(false);
  const [rolling, setRolling] = useState(false);
  /** Cosmetic faces during tumble (not the real outcome until commit) */
  const [tumbleFaces, setTumbleFaces] = useState(null);
  const [landTick, setLandTick] = useState(0);
  const longPressTimer = useRef(null);
  const tumbleIntervalRef = useRef(null);

  const { preset, custom, lastRoll } = dice;

  const commitRoll = useCallback(
    (result, motionReduced) => {
      const { faces, modifier, total, seed, isNat20, entry } = result;
      setNat20(!!isNat20 && !motionReduced);
      if (isNat20) window.setTimeout(() => setNat20(false), 700);
      setDice((d) => ({
        ...d,
        lastRoll: { faces, modifier, total, seed },
        history: [entry, ...(d.history || []).slice(0, 19)],
      }));
      setLandTick((n) => n + 1);
    },
    [setDice]
  );

  const doRoll = useCallback(() => {
    const spec = specFromPreset(dice.preset, dice.custom);
    const motionReduced = settings.reduceMotion;
    const skipAnim = motionReduced || process.env.NODE_ENV === 'test';

    if (tumbleIntervalRef.current) {
      window.clearInterval(tumbleIntervalRef.current);
      tumbleIntervalRef.current = null;
    }

    if (skipAnim) {
      setRolling(false);
      setTumbleFaces(null);
      commitRoll(buildRollResult(spec), motionReduced);
      return;
    }

    const slots = specToSlots(spec);
    if (slots.length === 0) return;

    setRolling(true);
    const tickMs = 72;
    const iterations = 7 + Math.floor(Math.random() * 6);

    const tick = () => {
      setTumbleFaces(slots.map((s) => ({ sides: s.sides, value: randomFaceValue(s.sides) })));
    };
    tick();

    let count = 0;
    tumbleIntervalRef.current = window.setInterval(() => {
      count += 1;
      if (count < iterations) {
        tick();
      } else {
        if (tumbleIntervalRef.current) {
          window.clearInterval(tumbleIntervalRef.current);
          tumbleIntervalRef.current = null;
        }
        const result = buildRollResult(spec);
        commitRoll(result, motionReduced);
        setRolling(false);
        setTumbleFaces(null);
      }
    }, tickMs);
  }, [dice.preset, dice.custom, settings.reduceMotion, commitRoll]);

  useEffect(() => {
    return () => {
      if (tumbleIntervalRef.current) window.clearInterval(tumbleIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (!rollMode) return undefined;
    let remove = () => {};
    const tid = window.setTimeout(() => {
      const onClick = (e) => {
        if (e.target.closest && e.target.closest('[data-dice-roll-mode-exit]')) return;
        doRoll();
      };
      window.addEventListener('click', onClick, true);
      remove = () => window.removeEventListener('click', onClick, true);
    }, 0);
    return () => {
      window.clearTimeout(tid);
      remove();
    };
  }, [rollMode, doRoll]);

  const onRollMouseDown = () => {
    longPressTimer.current = window.setTimeout(() => setRollMode(true), 500);
  };
  const onRollMouseUp = () => {
    if (longPressTimer.current) window.clearTimeout(longPressTimer.current);
  };

  const copyLast = () => {
    const h = dice.history && dice.history[0];
    if (!h) return;
    copyText(h.label).then((ok) => window.alert(ok ? 'Copied' : 'Failed'));
  };

  const copyAll = () => {
    const lines = (dice.history || []).map((h) => h.label);
    copyText(lines.join('\n')).then((ok) => window.alert(ok ? 'Copied' : 'Failed'));
  };

  return (
    <div className="dice-layout two-pane">
      <Link to="/" className="kit-back-link" style={{ gridColumn: '1 / -1' }}>
        ← Game Night Kit
      </Link>
      <h1 className="screen-title" style={{ gridColumn: '1 / -1' }}>
        Dice
      </h1>

      <div className="screen-panel">
        <div className="dice-chips">
          {['D6', '2D6', 'D20', 'CUSTOM'].map((p) => (
            <button
              type="button"
              key={p}
              className={`dice-chip ${preset === p ? 'active' : ''}`}
              onClick={() => (p === 'CUSTOM' ? setShowCustom(true) : setDice((d) => ({ ...d, preset: p })))}
            >
              {p === 'CUSTOM' ? 'Custom' : p}
            </button>
          ))}
        </div>

        <div className="dice-faces" aria-busy={rolling} aria-live="polite">
          {rolling && tumbleFaces
            ? tumbleFaces.map((f, i) => (
                <span key={`t-${i}`} className="dice-face dice-face--rolling dice-face--cube">
                  <span className="dice-face-inner">{renderFace(f.sides, f.value)}</span>
                </span>
              ))
            : lastRoll && lastRoll.faces
              ? lastRoll.faces.map((f, i) => (
                  <span
                    key={`${lastRoll.seed}-${landTick}-${i}`}
                    className={`dice-face dice-face--cube dice-face--land ${nat20 && f.sides === 20 && f.value === 20 ? 'nat20' : ''}`}
                  >
                    <span className="dice-face-inner">{renderFace(f.sides, f.value)}</span>
                  </span>
                ))
              : null}
          {!rolling && !lastRoll?.faces?.length && <span className="dice-face dice-face--cube dice-face--placeholder">?</span>}
        </div>

        <div className="dice-total-line">
          Total:{' '}
          {rolling ? (
            <span className="dice-total-pulse">…</span>
          ) : lastRoll ? (
            <span className="dice-total-value">{lastRoll.total}</span>
          ) : (
            '—'
          )}
        </div>

        <button
          type="button"
          className="dice-roll-btn"
          onClick={() => doRoll()}
          onMouseDown={onRollMouseDown}
          onMouseUp={onRollMouseUp}
          onMouseLeave={onRollMouseUp}
          onTouchStart={onRollMouseDown}
          onTouchEnd={onRollMouseUp}
        >
          Roll
        </button>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: 8 }}>
          Long-press Roll for roll mode (tap anywhere to roll).
        </p>
      </div>

      <div className="dice-history">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
          <strong>History</strong>
          <span>
            <button type="button" className="dice-chip" style={{ marginRight: 8 }} onClick={copyLast}>
              Copy last
            </button>
            <button type="button" className="dice-chip" onClick={copyAll}>
              Copy all
            </button>
          </span>
        </div>
        {(dice.history || []).length === 0 && <div className="dice-history-row">No rolls yet</div>}
        {(dice.history || []).map((h) => (
          <div key={h.ts} className="dice-history-row">
            {h.label}
          </div>
        ))}
      </div>

      {rollMode && (
        <div className="dice-roll-mode-banner">
          <button type="button" data-dice-roll-mode-exit style={{ fontWeight: 800, cursor: 'pointer' }} onClick={() => setRollMode(false)}>
            Exit roll mode
          </button>
          {' · '}
          Tap anywhere to roll
        </div>
      )}

      {showCustom && (
        <CustomModal
          custom={custom}
          onSave={(next) => {
            setDice((d) => ({ ...d, preset: 'CUSTOM', custom: next }));
            setShowCustom(false);
          }}
          onClose={() => setShowCustom(false)}
        />
      )}
    </div>
  );
}

function CustomModal({ custom, onSave, onClose }) {
  const [sides, setSides] = useState(custom.dice[0]?.sides || 6);
  const [count, setCount] = useState(custom.dice[0]?.count || 1);
  /** String so users can type negatives (input type="number" + Number() breaks on "-" mid-edit). */
  const [modifierStr, setModifierStr] = useState(() => String(custom.modifier ?? 0));

  return (
    <div className="dice-modal-back" role="dialog" aria-modal="true">
      <div className="dice-modal">
        <h2 className="screen-title" style={{ fontSize: '1.3rem' }}>
          Custom roll
        </h2>
        <label htmlFor="d-count">Number of dice (1–10)</label>
        <input id="d-count" type="number" min="1" max="10" value={count} onChange={(e) => setCount(Number(e.target.value))} />
        <label htmlFor="d-sides">Sides</label>
        <select id="d-sides" value={sides} onChange={(e) => setSides(Number(e.target.value))}>
          {[3, 4, 6, 8, 10, 12, 20, 100].map((s) => (
            <option key={s} value={s}>
              d{s}
            </option>
          ))}
        </select>
        <label htmlFor="d-mod">Modifier (-99 to 99)</label>
        <input
          id="d-mod"
          type="number"
          min="-99"
          max="99"
          value={modifierStr}
          onChange={(e) => setModifierStr(e.target.value)}
        />
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button
            type="button"
            className="dice-roll-btn"
            style={{ flex: 1 }}
            onClick={() => {
              const n = parseInt(modifierStr, 10);
              const modifier = Number.isFinite(n) ? Math.min(99, Math.max(-99, n)) : 0;
              onSave({ dice: [{ sides, count }], modifier });
            }}
          >
            Save
          </button>
          <button type="button" className="dice-chip" style={{ flex: 0 }} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
